import { AddressBridge } from '@/views/address/bridge'
import { anyToDate } from 'vtils/date'
import {
  apiActivityActivityGetActivityCommodity,
  ApiActivityActivityGetActivityCommodityResponse,
  apiActivityVirtualActivityFindCommodityActivityInfo,
  ApiActivityVirtualActivityFindCommodityActivityInfoResponse,
  apiActivityVirtualActivityFindVirtualActivityDetailVo,
  ApiActivityVirtualActivityFindVirtualActivityDetailVoResponse,
  apiCollectionCommodityAdd,
  apiCollectionCommodityCancel,
  apiCollectionCommodityStatus,
  apiCommodityCommodityDetail,
  ApiCommodityCommodityDetailResponse,
  apiCommodityCommodityGetIntegralInfo,
  ApiCommodityCommodityGetIntegralInfoResponse,
  apiCommodityCommodityMarketingDetail,
  ApiCommodityCommodityMarketingDetailResponse,
  apiCommoditySkuStockDetailByCommodity,
  apiCommodityVirtualExchangeGetSkuStock,
  ApiCommodityVirtualExchangeGetSkuStockRequest,
  apiEvaluationEvaluateGetLabelList,
  ApiEvaluationEvaluateGetLabelListResponse,
  apiEvaluationEvaluateGetLatestEvaluateList,
  ApiEvaluationEvaluateGetLatestEvaluateListResponse,
  apiEvaluationEvaluateList,
  ApiMerchantMerchantListByIdsResponse,
} from '@/api'
import { AsyncReturnType, OmitStrict, RequiredDeep } from 'vtils/types'
import {
  calcSkuId,
  CountdownInfo,
  createModelPlus,
  getCountdownInfo,
  getDisplayPrice,
  getGoodsBonus,
  getSeckillInfo,
  prepareData,
  SeckillInfo,
  submit,
  Track,
} from '@/utils'
import { dcb } from '@dcb/jssdk'
import { min, sumBy } from 'vtils'
import { redirectTo } from '@/router'

export interface RawGoodsData
  extends RequiredDeep<ApiCommodityCommodityDetailResponse> {
  /** 剩余库存 */
  surplusStock: number

  /** SKU 列表 */
  skuList: Array<
    RequiredDeep<ApiCommodityCommodityDetailResponse>['skuList'][0] & {
      /** 剩余库存 */
      surplusStock: number
      /** 拼团价格 */
      groupBuyingPrice: number
      /** 需多彩豆数量 */
      dcdCount: number
    }
  >
  /** 是否兑换商品 */
  isExchange: boolean

  /** 虚拟活动下控制购物车显示 */
  virtualActivityShowCart: number

  /** 虚拟商品sku信息 */
  virtualSkuInfo: {
    /** 总评论 */
    cutStock: number
    /** 好评率(99%) */
    skuId: string
  }

  /** 可见的 SKU 列表 */
  visibleSkuList: RawGoodsData['skuList']

  /** 选中的 SKU */
  selectedSku: RawGoodsData['skuList'][0]

  /** 购买数量 */
  buyCount: number

  /** 最小购买数量 */
  buyCountMin: number

  /** 最大购买数量 */
  buyCountMax: number

  /** 店铺状态 */
  storeStatus: number

  /** 规格列表 */
  specList: Array<{
    /** 规格 ID */
    id: string
    /** 规格名称 */
    name: string
    /** 规格值列表 */
    values: Array<{
      /** 规格 ID */
      pid: string
      /** 规格值 ID */
      id: string
      /** 规格值名称 */
      name: string
    }>
  }>

  getVisibleSkuBySpecValue(
    specValue: this['specList'][0]['values'][0],
    index: number,
  ): this['visibleSkuList'][0]

  /** 规格值库存 */
  specValueStock: Record<string, number>

  /** 秒杀数据 */
  seckillData: RequiredDeep<ApiActivityActivityGetActivityCommodityResponse>['activityData']

  /** 秒杀信息 */
  seckillInfo: SeckillInfo

  /** 营销数据 */
  marketingData: RequiredDeep<ApiCommodityCommodityMarketingDetailResponse>

  /** 商品虚拟活动信息 */
  virtualActivityData: RequiredDeep<ApiActivityVirtualActivityFindVirtualActivityDetailVoResponse>

  /** 通过触发物查询参加的所有活动信息 */
  commodityActivityData: RequiredDeep<ApiActivityVirtualActivityFindCommodityActivityInfoResponse>

  /** 折扣倒计时信息 */
  discountCountdownInfo: CountdownInfo

  /** 积分数据 */
  pointData: RequiredDeep<ApiCommodityCommodityGetIntegralInfoResponse>

  /** 评论数据 */
  commentData: {
    /** 总评论 */
    total: number
    /** 好评率(99%) */
    goodRate: string
    /** 标签列表 */
    tags: RequiredDeep<ApiEvaluationEvaluateGetLabelListResponse>
    /** 最新评论列表 */
    latestComments: RequiredDeep<ApiEvaluationEvaluateGetLatestEvaluateListResponse>['list']
  }

  /** 是否已下架 */
  isOff: boolean

  /** 是否已收藏 */
  isFavorited: boolean

  /** 是否虚拟商品 */
  isVirtual: boolean

  /** 是否有秒杀商品状态 */
  hasSeckill: boolean

  /** 是否秒杀商品 */
  isSeckill: boolean

  /** 是否品牌升级商品 */
  isBrand: boolean

  /** 是否拼团商品 */
  isGroupBuying: boolean

  /** 是否积分商品 */
  isPoint: boolean

  /** 是否多彩豆商品 */
  isDcd: boolean

  /** 多彩豆最低数量 */
  dcdMinCount: number

  /** 是否已售罄 */
  isSold: boolean

  /** 是否有活动 */
  hasActivity: boolean

  /** 是否有资质信息 */
  hasCert: boolean

  /** 是否隐藏价格 */
  hidePrice: boolean

  /** 保障文本 */
  safeguardText: string

  /** 奖励文本 */
  bonusText: string

  /** 快递文本 */
  expressText: string

  /** 显示的价格 */
  displayPrice: string

  /** 显示的价格前缀 */
  displayPricePrefix: string

  /** 可用的 SKU 列表 */
  availableSkuList: Array<{
    /** SKU ID */
    skuId: string
    /** 价格 */
    price?: number
    /** 拼团价 */
    groupBuyingPrice?: number
    /** 多彩豆数量 */
    dcdCount?: number
    /** 库存 */
    stock?: number
  }>

  /** SKU 选择器 */
  skuPicker: {
    /** 是否可见 */
    visible: boolean
    /** 操作 */
    action: 'addCart' | 'buy' | 'favorite' | 'groupBuying' | 'pure'
    /** 团购订单 ID */
    groupBuyingOrderId?: string
    /** 回调 */
    callback?: (goodData: GoodsData) => any
  }

  shareData: any

  /** 收藏 */
  favorite: () => Promise<void>

  /** 刷新秒杀 */
  refreshSeckill: () => Promise<void>

  /** 刷新营销 */
  refreshMarketing: () => Promise<void>

  /** 获取商品虚拟活动信息 */
  findVirtualActivityDetailVo: () => Promise<void>
  /** 通过触发物查询参加的所有活动信息 */
  findCommodityActivityInfo: () => Promise<void>

  /** 根据 SKU 刷新 */
  refreshBySku: () => Promise<void>

  /** 显示 SKU 选择器 */
  showSkuPicker: (payload: OmitStrict<this['skuPicker'], 'visible'>) => void

  /** 关闭 SKU 选择器 */
  closeSkuPicker: () => void

  /** 分享 */
  share: () => void

  /** 检测商品状态 */
  checkGoodsStatus: (goodsId?: string) => Promise<boolean>

  /** 页面激活时是否不刷新 */
  noRefresh: boolean

  /** 自提点 */
  selfPickupStation: RequiredDeep<ApiMerchantMerchantListByIdsResponse>['list'][0]

  /** 发货类型 0正常发货 1门店自提  */
  shipType: number
}

export type GoodsData = NonNullable<AsyncReturnType<typeof getGoodsData>>

/**
 * 获取商品数据。
 */
export async function getGoodsData(
  this: any,
  {
    goodsId,
    availableSkuList,
    simple,
    initialSkuId,
    initialBuyCount,
    parentCommodityId,
    skuId,
    isExchange,
    previewStatus,
  }: {
    goodsId: string
    availableSkuList?: RawGoodsData['availableSkuList']
    simple?: boolean
    /** 初始 SKU ID */
    initialSkuId?: string
    /** 初始购买数量 */
    initialBuyCount?: number
    parentCommodityId?: string
    skuId?: string
    isExchange?: boolean
    previewStatus?: number //是否预览0否 1是
  },
) {
  // 获取商品原始数据
  let rawGoodsData: RequiredDeep<ApiCommodityCommodityDetailResponse> | null =
    (await apiCommodityCommodityDetail(
      {
        commodityId: goodsId,
        previewStatus: previewStatus ?? 0, // 修复：为 previewStatus 提供默认值 0
      },
      {
        noErrorToast: true,
      },
    ).catch<null>(() => null)) as any

  if (rawGoodsData) {
    const track_from = Track.getFrom()

    // 兼容标电组合商品
    if (rawGoodsData.commodityType === 5) {
      redirectTo('JointStdGoodsIndex', {
        id: goodsId,
        track_from,
      })
      return
    }

    // 兼容旅游商品
    if (rawGoodsData.commodityType === 4) {
      redirectTo('TravelGoodsIndex', {
        goodsId: goodsId,
        track_from,
      })
      return
    }

    // 兼容标销商品
    if (rawGoodsData.commodityType === 1) {
      redirectTo('BookGoodsIndex', {
        goodsId: goodsId,
        track_from,
      })
      return
    }

    // 兼容组合商品
    if (rawGoodsData.commodityType === 3) {
      redirectTo('JointGoodsIndex', {
        goodsId: goodsId,
        track_from,
      })
      return
    }

    // 兼容配送商品
    if (rawGoodsData.commodityType === 6) {
      redirectTo('InstantDeliveryGoodsIndex', {
        goodsId: goodsId,
        track_from,
      })
      return
    }

    // 过滤掉停用的 SKU
    rawGoodsData.skuList = rawGoodsData.skuList.filter(
      sku => sku.skuStatus === 1,
    )

    // 没有设置图片的 SKU 默认取商品封面图
    rawGoodsData.skuList.forEach(item => {
      if (!item.skuPic) {
        item.skuPic = rawGoodsData!.coverPic
      }
    })

    if (rawGoodsData.skuList.length === 0) {
      rawGoodsData = null
    }
  }

  if (!rawGoodsData) {
    return createModelPlus({
      isOff: true,
    } as RawGoodsData)
  }

  const goodsModel = createModelPlus<RawGoodsData>({
    ...{},
    ...rawGoodsData,
    shipType: 0,
    selfPickupStation: {} as any,
    noRefresh: false,
    isOff: false,
    skuList: [],
    skuPicker: {
      action: 'buy',
      visible: false,
    },
    get availableSkuList() {
      if (availableSkuList) {
        return availableSkuList
      }
      if (this.isSeckill) {
        return this.seckillData.commodityInfo.skuInfo.map<
          RawGoodsData['availableSkuList'][0]
        >(sku => ({
          skuId: sku.skuId,
          price: sku.price,
          stock: sku.stock,
        }))
      }
      if (this.isDcd) {
        return this.marketingData.integrals.map<
          RawGoodsData['availableSkuList'][0]
        >(sku => ({
          skuId: sku.skuId,
          price: sku.discountPrice,
          dcdCount: sku.integral,
          stock: sku.stock,
        }))
      }
      return this.skuList.map<RawGoodsData['availableSkuList'][0]>(sku => ({
        skuId: sku.skuId,
      }))
    },
    get visibleSkuList() {
      return this.skuList.reduce<RawGoodsData['visibleSkuList']>((res, sku) => {
        const availableSku = this.availableSkuList.find(
          x => x.skuId === sku.skuId,
        )
        if (availableSku) {
          sku.commoditySkuPrice = availableSku.price ?? sku.commoditySkuPrice
          sku.groupBuyingPrice =
            availableSku.groupBuyingPrice ?? sku.groupBuyingPrice
          sku.dcdCount = availableSku.dcdCount ?? sku.dcdCount
          sku.surplusStock = availableSku.stock ?? sku.surplusStock
          res.push(sku)
        }
        return res
      }, [])
    },
    selectedSku: {} as any,
    get surplusStock() {
      return sumBy(this.visibleSkuList, sku => sku.surplusStock)
    },
    buyCount: 0,
    get buyCountMin() {
      if (this.isSeckill) {
        return 1
      }
      const 剩余库存 = Math.min(
        this.selectedSku.surplusStock,
        this.surplusStock,
      )
      return Math.min(Math.max(1, this.beginSaleQuantity), 剩余库存)
    },
    get buyCountMax() {
      if (this.isSeckill) {
        // 取限购和剩余库存的最小值
        return Math.min(
          // 限购为0表示不限购
          this.seckillData.commodityInfo.limitCount ||
            this.seckillData.commodityInfo.surplusStock,
          this.seckillData.commodityInfo.surplusStock,
        )
      }
      const 剩余库存 = Math.min(
        this.selectedSku.surplusStock,
        this.surplusStock,
      )
      const 单日限量 = Math.min(
        this.selectedSku.singleDayLimitPurchases || 剩余库存,
        剩余库存,
      )
      const 单用户限量 = Math.min(
        this.selectedSku.userDayLimitPurchases || 单日限量,
        单日限量,
      )
      return 单用户限量
    },
    get specList() {
      return this.visibleSkuList.reduce<RawGoodsData['specList']>(
        (res, sku) => {
          sku.skuSpecs.forEach(item => {
            let specIndex = res.findIndex(x => x.id === item.skuAttributeId)
            if (specIndex === -1) {
              specIndex = res.length
              res.push({
                id: item.skuAttributeId,
                name: item.attributeName,
                values: [],
              })
            }
            const specValueIndex = res[specIndex].values.findIndex(
              x => x.id === item.selected.id,
            )
            if (specValueIndex === -1) {
              res[specIndex].values.push({
                pid: item.skuAttributeId,
                id: item.selected.id,
                name: item.selected.value,
              })
            }
          })
          return res
        },
        [],
      )
    },
    getVisibleSkuBySpecValue(specValue, index) {
      return this.visibleSkuList.find(
        sku =>
          sku.skuId ===
          calcSkuId([
            this.commodityId,
            ...this.selectedSku.skuSpecs
              .map<string[]>((item, i) => {
                return i === index
                  ? [specValue.pid, specValue.id]
                  : [item.skuAttributeId, item.selected.id]
              })
              .flat(),
          ]),
      )!
    },
    get specValueStock() {
      const stock: Record<string, number> = {}
      const selectedSpecValueIds = this.selectedSku.skuSpecs.map(
        spec => spec.selected.id,
      )
      this.specList.forEach((item, index) => {
        item.values.forEach(specValue => {
          if (selectedSpecValueIds.includes(specValue.id)) {
            stock[specValue.id] = this.selectedSku.surplusStock
          } else {
            stock[specValue.id] =
              this.getVisibleSkuBySpecValue(specValue, index)?.surplusStock || 0
          }
        })
      })
      return stock
    },
    seckillData: {} as any,
    seckillInfo: {} as any,
    marketingData: {} as any,
    commodityActivityData: {} as any,
    virtualActivityData: {} as any,
    virtualActivityShowCart: 1,
    discountCountdownInfo: {} as any,
    pointData: {} as any,
    commentData: {} as any,
    isFavorited: false,
    isExchange: false,
    /** 虚拟商品sku信息 */
    virtualSkuInfo: {} as any,
    get isVirtual() {
      return this.commodityType === 2
    },
    get hasSeckill() {
      return !!this.seckillData.activityId
    },
    get isSeckill() {
      return (
        this.hasSeckill && getSeckillInfo(this.seckillData as any).isRunning
      )
    },
    get isBrand() {
      return !!this.marketingData.commodityBrand?.status
    },
    get isGroupBuying() {
      return !!this.marketingData.groupBuying?.hasActivity
    },
    get isPoint() {
      return !!this.pointData.belongToPoint
    },
    get isDcd() {
      return !!this.marketingData.integrals?.length && !this.isSeckill
    },
    get dcdMinCount() {
      return this.isDcd
        ? min(this.marketingData.integrals.map(sku => sku.integral))!
        : 0
    },
    get isSold() {
      return !this.surplusStock
    },
    get hasActivity() {
      return (
        // 非拼团
        !this.isGroupBuying &&
        // 且有活动
        !!this.marketingData?.showTemplate?.hasTemplate
      )
    },
    get hasCert() {
      return !!this.storeQualification.length
    },
    get hidePrice() {
      return (
        // 有秒杀
        this.isSeckill ||
        // 并且有图文类型的活动模板
        (this.hasActivity &&
          this.marketingData.showTemplate.templateType === 1) ||
        !!this.commodityActivityData.hasDiscount
      )
    },
    get safeguardText() {
      return this.saleAfterServiceType?.includes(0) ? '7天无理由售后' : ''
    },
    bonusText: '',
    get expressText() {
      return this.postType === 1 ? '包邮' : '不包邮'
    },

    get displayPrice() {
      return this.isGroupBuying
        ? `${this.selectedSku.groupBuyingPrice}`
        : // this.selectedSku.commoditySkuPrice
          // 价格改为区间显示
          getDisplayPrice(this)
    },

    get displayPricePrefix() {
      return this.isGroupBuying ? '拼团价' : ''
    },

    get shareData() {
      return {
        title: this.commodityTitle,
        desc: this.commoditySubTitle,
        url: location.href,
        cover: this.isBrand
          ? this.marketingData.commodityBrand.mainImage
          : this.coverPic,
        // 品牌升级商品才需要海报
        showImage: this.isBrand ? 1 : 0,
        paramsFree: {},
        resourceList: [
          {
            resourceId: this.commodityId as any,
            resourceName: this.commodityTitle as any,
          } as any,
        ],
      }
    },

    // 收藏操作
    async favorite() {
      await submit(async $ => {
        if (this.isFavorited) {
          $.start('取消收藏中...')
          await apiCollectionCommodityCancel({
            skuIdList: [this.selectedSku.skuId],
          })
          this.isFavorited = false
          await $.success('取消收藏成功')
        } else {
          $.start('收藏中...')
          await apiCollectionCommodityAdd({
            commodityId: goodsId,
            fromShoppingCartFlag: 0,
            skuId: this.selectedSku.skuId,
          })
          this.isFavorited = true
          await $.success('收藏成功')
        }
      })
    },

    // 刷新秒杀
    async refreshSeckill() {
      await submit(async $ => {
        $.start()
        const {
          // 获取秒杀数据
          seckillData,
        } = await prepareData({
          seckillData: apiActivityActivityGetActivityCommodity({
            commodityId: goodsId,
          }),
        })
        this.seckillData = ((seckillData.hasActivity &&
          seckillData.activityData) ||
          {}) as any
      })
    },

    // 刷新营销
    async refreshMarketing() {
      await submit(async $ => {
        $.start()
        const {
          // 获取营销数据
          marketingData,
        } = await prepareData({
          marketingData: apiCommodityCommodityMarketingDetail({
            commodityId: goodsId,
            skuId: this.selectedSku.skuId,
          }),
        })
        this.marketingData = marketingData as any
      })
    },
    // 获取商品虚拟活动信息
    async findVirtualActivityDetailVo() {
      await submit(async $ => {
        $.start()
        const {
          // 获取营销数据
          virtualActivityData,
        } = await prepareData({
          virtualActivityData:
            apiActivityVirtualActivityFindVirtualActivityDetailVo({
              commodityId: goodsId,
            }),
        })
        this.virtualActivityData = virtualActivityData as any
        this.virtualActivityShowCart = virtualActivityData
          ? [1, 0].includes(virtualActivityData.shopCarStatus as any)
            ? virtualActivityData.shopCarStatus
            : 1
          : (1 as any)
      })
    },
    // 通过触发物查询参加的所有活动信息
    async findCommodityActivityInfo() {
      await submit(async $ => {
        $.start()
        const {
          // 获取营销数据
          commodityActivityData,
        } = await prepareData({
          commodityActivityData:
            apiActivityVirtualActivityFindCommodityActivityInfo({
              commodityId: goodsId,
              skuList: this.skuList,
            }),
        })
        this.commodityActivityData = commodityActivityData as any
      })
    },

    // 根据 SKU 刷新
    async refreshBySku() {
      const {
        // 获取营销数据
        marketingData,
        // 获取收藏状态
        isFavorited,
        // 获取奖励文本
        bonusText,
      } = await prepareData({
        marketingData: apiCommodityCommodityMarketingDetail({
          commodityId: goodsId,
          skuId: this.selectedSku.skuId,
        }),
        // .then(res => {
        //   res.channelIntegrals = [
        //     {
        //       activityId: 1,
        //       describe: '该商品支持工会卡积分抵扣支付',
        //     },
        //     {
        //       activityId: 13,
        //       describe: '该商品支持工会卡积分抵扣支付',
        //     },
        //   ]
        //   return res
        // })
        isFavorited: simple
          ? Promise.resolve(false)
          : apiCollectionCommodityStatus({
              skuId: this.selectedSku.skuId,
            }).then(res => !!res.status),
        bonusText: getGoodsBonus(this.selectedSku, this.buyCount),
      })
      this.marketingData = marketingData as any
      this.isFavorited = isFavorited
      this.bonusText = bonusText
    },

    // 显示 SKU 选择器
    showSkuPicker(payload) {
      this.skuPicker = {
        ...payload,
        visible: true,
      }
    },

    // 关闭 SKU 选择器
    closeSkuPicker() {
      this.skuPicker = {
        action: 'buy',
        visible: false,
      }
    },

    share() {
      // 传参参考
      // https://yapi.gogpay.cn/project/2193/interface/api/47519
      dcb.share(this.shareData)
    },

    async checkGoodsStatus(_goodsId = goodsId) {
      return apiCommodityCommodityDetail(
        {
          commodityId: _goodsId,
        },
        {
          noErrorToast: true,
        },
      ).then(
        () => true,
        () => false,
      )
    },
  })

  // 获取当前地址
  const locationData = await AddressBridge.getCurrentLocation()

  const preOrderRequestData: ApiCommodityVirtualExchangeGetSkuStockRequest = {
    commodityId: goodsId,
    parentCommodityId: parentCommodityId,
    skuId: skuId,
    commodityType: '0',
  }

  if (isExchange) {
    goodsModel.isExchange = true
    const virtualSkuInfo = await apiCommodityVirtualExchangeGetSkuStock(
      preOrderRequestData,
    )

    goodsModel.virtualSkuInfo = {
      cutStock: virtualSkuInfo.cutStock || 0,
      skuId: virtualSkuInfo.skuId || '',
    }
  }

  goodsModel.findVirtualActivityDetailVo()

  const {
    // 获取库存数据
    stockData,
    // 获取秒杀数据
    seckillData,
    // 获取积分数据
    pointData,
    // 获取评论数据
    commentData,
  } = await prepareData({
    stockData: apiCommoditySkuStockDetailByCommodity({
      commodityId: goodsId,
      provinceCode: locationData.provinceCode,
      cityCode: locationData.cityCode,
      areaCode: locationData.districtCode,
    }),
    seckillData: simple
      ? ({} as ApiActivityActivityGetActivityCommodityResponse)
      : apiActivityActivityGetActivityCommodity({
          commodityId: goodsId,
        }),
    pointData: simple
      ? ({} as ApiCommodityCommodityGetIntegralInfoResponse)
      : apiCommodityCommodityGetIntegralInfo({
          commodityId: goodsId,
        }),
    commentData: simple
      ? ({
          total: 0,
          goodRate: '',
          latestComments: [],
          tags: [],
        } as RawGoodsData['commentData'])
      : Promise.all([
          // 获取评论总数
          apiEvaluationEvaluateList({
            commodityServiceId: goodsId,
            pageIndex: 1,
            pageSize: 1,
            havingPicFlag: 0,
          }),
          // 获取最新评论、好评率
          apiEvaluationEvaluateGetLatestEvaluateList({
            commodityServiceId: goodsId,
            pageIndex: 1,
            pageSize: 1,
          }),
          // 获取评论标签
          apiEvaluationEvaluateGetLabelList({
            commodityServiceId: goodsId,
          }),
        ]).then<RawGoodsData['commentData']>(
          ([totalSource, latestSource, tagSource]) => {
            return {
              total: totalSource.total,
              goodRate: latestSource.praiseRate as any,
              latestComments: latestSource.list as any,
              tags: tagSource as any,
            }
          },
        ),
  })

  goodsModel.seckillData = ((seckillData.hasActivity &&
    seckillData.activityData) ||
    {}) as any
  goodsModel.pointData = pointData as any
  goodsModel.commentData = commentData as any

  goodsModel.skuList = rawGoodsData.skuList.map(sku => {
    return {
      ...sku,
      surplusStock: stockData.find(x => x.skuId === sku.skuId)!.surplusStock!,
      groupBuyingPrice: 0,
      dcdCount: 0,
    }
  })

  goodsModel.selectedSku =
    (initialSkuId &&
      goodsModel.visibleSkuList.find(sku => sku.skuId === initialSkuId)) ||
    goodsModel.visibleSkuList.find(sku => sku.defaultShow === 1) ||
    goodsModel.visibleSkuList[0]
  goodsModel.buyCount = initialBuyCount || goodsModel.buyCountMin

  await goodsModel.$watchResolveOnFirstTrigger(
    () => goodsModel.selectedSku.skuId,
    () => goodsModel.refreshBySku(),
    {
      immediate: true,
    },
  )

  await goodsModel.$watchResolveOnFirstTrigger(
    () => goodsModel.marketingData.groupBuying,
    () => {
      if (goodsModel.marketingData.groupBuying?.skuInfos?.length) {
        goodsModel.marketingData.groupBuying.skuInfos.forEach(sku => {
          const foundSku = goodsModel.skuList.find(x => x.skuId === sku.skuId)
          if (foundSku && !foundSku.groupBuyingPrice) {
            foundSku.groupBuyingPrice = sku.maxPrice
          }
        })
      }
    },
    {
      immediate: true,
    },
  )
  await goodsModel.$watchResolveOnFirstTrigger(
    () => goodsModel.skuList,
    () => {
      if (goodsModel.skuList && goodsModel.skuList.length)
        goodsModel.findCommodityActivityInfo()
    },
    {
      immediate: true,
    },
  )

  // 是秒杀商品，倒计时
  if (goodsModel.hasSeckill) {
    const { dispose } = getSeckillInfo(goodsModel.seckillData as any, info => {
      goodsModel.seckillInfo = info
      if (info.isEnded) {
        dispose?.()
        goodsModel.refreshSeckill()
      }
    })
    goodsModel.$beforeDestroy(() => dispose?.())
  }
  // 或者有活动且是折扣活动，倒计时
  else if (
    goodsModel.hasActivity &&
    goodsModel.marketingData.showTemplate.templateSource === 0
  ) {
    const { dispose } = getCountdownInfo({
      startDate: anyToDate(goodsModel.marketingData.showTemplate.startTime),
      endDate: anyToDate(goodsModel.marketingData.showTemplate.endTime),
      onChange: info => {
        goodsModel.discountCountdownInfo = info
        if (info.isEnded) {
          dispose?.()
          goodsModel.refreshMarketing()
        }
      },
    })
    goodsModel.$beforeDestroy(() => dispose?.())
  }

  return goodsModel
}
